import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navigation, Footer } from '../components/landing';
import { localeFromPath } from '../i18n/path-locale';
import faqClusterData from './faq/faqClusterContent';

// Topic detail pages (Ticket 5). EN/FR routes per topic; link text is the
// locked page title (minus the " | AidFone" suffix). 3 placeholder slots
// stand in for the batch-2 pages gated behind the week-4 indexation check.
const FAQ_TOPICS = [
  { en: '/faq/living-alone-and-far-away', fr: '/fr/faq/parent-seul-a-distance' },
  { en: '/faq/memory-vision-movement', fr: '/fr/faq/memoire-vision-mobilite' },
  { en: '/faq/price-trial-cancelling', fr: '/fr/faq/prix-essai-annulation' },
] as const;

const topicLabel = (route: string): string =>
  (faqClusterData[route]?.title ?? '').replace(/\s*\|\s*AidFone\s*$/, '');

const TITLE_EN = 'Frequently asked questions — AidFone';
const TITLE_FR = 'Questions fréquentes — AidFone';

const FAQ_IDS = [
  'willTheyUse',
  'newPhone',
  'memoryLoss',
  'privacy',
  'cost',
  'refund',
  'devices',
  'iphoneDashboard',
  'caregivers',
] as const;

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    className={`w-5 h-5 text-brand-primary flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export const FAQ: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const locale = localeFromPath(location.pathname);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    document.title = locale === 'fr' ? TITLE_FR : TITLE_EN;
    document.documentElement.lang = locale === 'fr' ? 'fr-CA' : 'en';
  }, [locale]);

  return (
    <div className="min-h-screen bg-brand-bg font-body">
      <Navigation />

      <main className="pt-24">
        <section className="py-16">
          <div className="max-w-[800px] mx-auto px-6 text-center">
            <h1 className="font-display text-[clamp(2rem,5vw,3rem)] font-semibold mb-6 leading-tight text-brand-text">
              {t('pages.faq.title')}
            </h1>
            <p className="text-brand-text-light text-lg max-w-[600px] mx-auto">
              {t('pages.faq.intro')}
            </p>
          </div>
        </section>

        <section className="pb-4">
          <div className="max-w-[800px] mx-auto px-6">
            <h2 className="font-display text-[clamp(1.3rem,3.5vw,1.8rem)] font-semibold mb-6 text-brand-text">
              {locale === 'fr' ? 'Parcourir par sujet' : 'Browse by topic'}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {FAQ_TOPICS.map((topic) => {
                const route = locale === 'fr' ? topic.fr : topic.en;
                return (
                  <Link
                    key={route}
                    to={route}
                    className="group flex items-center justify-between gap-3 p-5 rounded-xl border border-brand-border bg-white hover:border-brand-primary hover:shadow-sm transition-all no-underline"
                  >
                    <span className="font-semibold text-brand-text leading-snug">
                      {topicLabel(route)}
                    </span>
                    <span
                      aria-hidden="true"
                      className="text-brand-primary text-xl flex-shrink-0 transition-transform group-hover:translate-x-1"
                    >
                      →
                    </span>
                  </Link>
                );
              })}
              {[0, 1, 2].map((i) => (
                <div
                  key={`placeholder-${i}`}
                  className="flex items-center gap-3 p-5 rounded-xl border border-dashed border-brand-border bg-brand-bg-warm text-brand-text-muted select-none"
                  aria-disabled="true"
                >
                  <span className="font-medium leading-snug">
                    {locale === 'fr' ? 'Bientôt disponible' : 'Coming soon'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-16 pt-12">
          <div className="max-w-[800px] mx-auto px-6">
            {FAQ_IDS.map((id) => {
              const base = `pages.faq.items.${id}`;
              const isOpen = openId === id;
              return (
                <div key={id} className="border-b border-brand-border">
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : id)}
                    className="w-full py-5 cursor-pointer flex justify-between items-center gap-4 font-semibold text-brand-text text-left bg-transparent border-0 outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded"
                    aria-expanded={isOpen}
                  >
                    <span>{t(`${base}.question`)}</span>
                    <ChevronIcon isOpen={isOpen} />
                  </button>
                  <div
                    className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                      isOpen ? 'max-h-[400px]' : 'max-h-0'
                    }`}
                  >
                    <p className="pb-5 text-brand-text-light text-[0.95rem] leading-relaxed">
                      {t(`${base}.answer`)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="py-16 bg-brand-bg-warm">
          <div className="max-w-[700px] mx-auto px-6 text-center">
            <h2 className="font-display text-[clamp(1.4rem,4vw,2rem)] font-semibold mb-4 text-brand-text">
              {t('pages.faq.ctaTitle')}
            </h2>
            <p className="text-brand-text-light mb-8 text-lg">
              {t('pages.faq.ctaBody')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/configure"
                className="inline-flex items-center justify-center px-7 py-3.5 font-body text-base font-semibold no-underline rounded-md bg-brand-accent text-white shadow-[0_4px_14px_rgba(224,123,76,0.35)] hover:bg-[#c96a3f] transition-all"
              >
                {t('pages.faq.ctaPrimary')}
              </Link>
              <Link
                to={locale === 'fr' ? '/fr/contact' : '/contact'}
                className="inline-flex items-center justify-center px-7 py-3.5 font-body text-base font-semibold no-underline rounded-md border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all"
              >
                {t('pages.faq.ctaSecondary')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
